interface CategorySEOContentProps {
  category: string;
}

const categoryContent: Record<string, {
  title: string;
  description: string;
  faq: { question: string; answer: string }[];
  schemaType: string;
}> = {
  restaurant: {
    title: 'Best Restaurants in Roseville CA',
    description: 'Discover the finest dining experiences in Roseville, California. From farm-to-table cuisine to wood-fired pizza and craft breweries, our curated selection of restaurants in Roseville CA offers something for every palate. Whether you\'re searching for family-friendly eateries, romantic date night spots, or casual lunch options, you\'ll find verified reviews, menus, and contact information for top-rated local restaurants.',
    faq: [
      {
        question: 'What are the best restaurants in Roseville CA?',
        answer: 'Top-rated restaurants in Roseville include The Rustic Table for farm-to-table dining, Roseville Pizza Kitchen for artisan wood-fired pizzas, Craft & Cork Wine Bar for upscale small plates, and Roseville Brewing Co. for craft beer and pub fare.'
      },
      {
        question: 'Are there family-friendly restaurants in Roseville?',
        answer: 'Yes, Roseville has many family-friendly dining options including Roseville Pizza Kitchen with a patio, Roseville Brewing Co. with a dog-friendly outdoor area, and numerous casual dining establishments throughout the city.'
      },
      {
        question: 'Which Roseville restaurants offer outdoor dining?',
        answer: 'Several Roseville restaurants feature outdoor seating including The Rustic Table\'s garden patio, Roseville Brewing Co.\'s dog-friendly patio, and Craft & Cork Wine Bar\'s seasonal outdoor area.'
      }
    ],
    schemaType: 'Restaurant'
  },
  health: {
    title: 'Dentists in Roseville CA - Top-Rated Dental Care',
    description: 'Find trusted dentists in Roseville CA offering comprehensive dental care for your entire family. Our directory features verified dental practices providing routine cleanings, cosmetic dentistry, orthodontics, and emergency dental services. Compare ratings, read patient reviews, and book appointments with experienced Roseville dentists who prioritize gentle, personalized care.',
    faq: [
      {
        question: 'How do I find a good dentist in Roseville CA?',
        answer: 'Look for dentists with high patient ratings (4.5+ stars), verified credentials, positive reviews mentioning gentle care and clear communication, and convenient office hours. Roseville Family Dental is a top-rated option with over 20 years of experience.'
      },
      {
        question: 'Do Roseville dentists accept new patients?',
        answer: 'Most Roseville dental practices accept new patients. Many offer special promotions for first-time visitors, such as discounted initial exams or cleanings. Contact the office directly to verify availability.'
      },
      {
        question: 'What dental services are available in Roseville?',
        answer: 'Roseville dentists offer comprehensive services including routine cleanings, fillings, crowns, root canals, teeth whitening, veneers, orthodontics, implants, and emergency dental care.'
      }
    ],
    schemaType: 'Dentist'
  },
  home: {
    title: 'Plumbers in Roseville California - Licensed & Trusted',
    description: 'Connect with licensed plumbers in Roseville California for all your residential and commercial plumbing needs. Our verified directory includes experienced plumbing professionals offering emergency repairs, drain cleaning, water heater installation, pipe replacement, and routine maintenance. Get fast, reliable service from top-rated Roseville plumbers with transparent pricing and quality workmanship.',
    faq: [
      {
        question: 'How do I find a reliable plumber in Roseville CA?',
        answer: 'Choose licensed, insured plumbers with strong customer reviews, transparent pricing, and emergency availability. Check for proper licensing through the California Contractors State License Board and read recent customer testimonials.'
      },
      {
        question: 'What plumbing services are available in Roseville?',
        answer: 'Roseville plumbers provide emergency repairs, drain cleaning, water heater installation and repair, pipe replacement, leak detection, sewer line services, fixture installation, and preventive maintenance.'
      },
      {
        question: 'Do Roseville plumbers offer emergency services?',
        answer: 'Yes, many Roseville plumbing companies offer 24/7 emergency services for urgent issues like burst pipes, severe leaks, clogged drains, and water heater failures.'
      }
    ],
    schemaType: 'Plumber'
  },
  beauty: {
    title: 'Hair Salons in Roseville CA - Expert Stylists',
    description: 'Discover premier hair salons in Roseville CA featuring award-winning stylists and comprehensive beauty services. From precision haircuts and vibrant color treatments to keratin smoothing and bridal styling, Roseville hair salons deliver exceptional results in luxurious settings. Browse verified reviews, view stylist portfolios, and book appointments at top-rated salons near you.',
    faq: [
      {
        question: 'What are the best hair salons in Roseville CA?',
        answer: 'Luxe Salon & Spa is a top-rated choice offering full-service hair care with award-winning stylists. Look for salons with high ratings, experienced colorists, and positive reviews mentioning customer service and results.'
      },
      {
        question: 'How much do hair services cost in Roseville?',
        answer: 'Hair service prices vary by salon and service type. Expect to pay $50-$100 for haircuts, $100-$250 for color services, and $150-$400 for specialty treatments like balayage or keratin. Many salons offer first-time client discounts.'
      },
      {
        question: 'Do Roseville hair salons offer bridal services?',
        answer: 'Yes, several Roseville salons specialize in bridal hair and makeup, offering trial sessions, day-of styling, and bridal party packages. Luxe Salon & Spa is known for their comprehensive bridal services.'
      }
    ],
    schemaType: 'HairSalon'
  },
  auto: {
    title: 'Auto Repair in Roseville CA - ASE-Certified Mechanics',
    description: 'Trust your vehicle to certified auto repair shops in Roseville CA. Our directory features ASE-certified mechanics providing expert service for all makes and models, including brake repair, engine diagnostics, transmission service, oil changes, tire installation, and preventive maintenance. Compare shops, read customer reviews, and find honest, affordable auto repair in Roseville California.',
    faq: [
      {
        question: 'How do I choose an auto repair shop in Roseville?',
        answer: 'Look for ASE-certified technicians, positive customer reviews, transparent pricing, warranty on repairs, and shops that specialize in your vehicle make. Sierra Auto Works and Precision Tire & Wheel are highly rated local options.'
      },
      {
        question: 'What auto services are available in Roseville CA?',
        answer: 'Roseville auto shops offer comprehensive services including brake repair, engine diagnostics, transmission service, oil changes, tire sales and installation, wheel alignment, AC repair, and scheduled maintenance.'
      },
      {
        question: 'Do Roseville auto shops offer free estimates?',
        answer: 'Most reputable Roseville auto repair shops provide free estimates for major repairs. Many also offer complimentary multi-point inspections with oil changes to identify potential issues early.'
      }
    ],
    schemaType: 'AutoRepair'
  }
};

export default function CategorySEOContent({ category }: CategorySEOContentProps) {
  const content = categoryContent[category];

  if (!content || category === 'all') {
    return null;
  }

  return (
    <div className="mb-12">
      {/* SEO Content Block */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">{content.title}</h1>
        <p className="text-sm text-gray-600 leading-relaxed">{content.description}</p>
      </div>

      {/* FAQ Section */}
      <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-2xl border border-teal-100 p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
            <i className="ri-question-line text-xl text-white"></i>
          </div>
          <h2 className="text-xl font-bold text-gray-900">Frequently Asked Questions</h2>
        </div>
        <div className="space-y-4">
          {content.faq.map((item, index) => (
            <details
              key={index}
              className="bg-white rounded-xl border border-gray-100 overflow-hidden group"
            >
              <summary className="px-6 py-4 cursor-pointer list-none flex items-center justify-between hover:bg-gray-50 transition-colors">
                <strong className="text-sm font-semibold text-gray-900 pr-4">
                  {item.question}
                </strong>
                <i className="ri-arrow-down-s-line text-xl text-teal-600 group-open:rotate-180 transition-transform flex-shrink-0"></i>
              </summary>
              <div className="px-6 pb-4 pt-2">
                <p className="text-sm text-gray-600 leading-relaxed">{item.answer}</p>
              </div>
            </details>
          ))}
        </div>
      </div>

      {/* Schema.org Markup */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: content.faq.map((item) => ({
              '@type': 'Question',
              name: item.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: item.answer
              }
            }))
          })
        }}
      />
    </div>
  );
}